import {
    type UiConfig,
    useConfigureUi,
} from '@frontend/hooks/useConfigureUi'
import {useCabinetContextApi} from '@frontend/modules/private/shared/CabinetContext'
import type {CabinetMenuType} from '@frontend/modules/private/shared/CabinetSharedTypes'
import type {DependencyList} from 'react'

/**
 * Настройки глобальных элементов интерфейса приложения для кабинета пользователя.
 * Значения по умолчанию:
 * @see getDefaults()
 */
export interface CabinetUiConfig extends UiConfig {
    // Тип бокового меню в кабинете пользователя.
    sideMenuType: CabinetMenuType
}

type DefaultCabinetUiConfig = Required<Omit<CabinetUiConfig, 'other' | 'onUnmount'>> & Pick<UiConfig, 'other' | 'onUnmount'>

// Значения по умолчанию.
function getDefaults(): DefaultCabinetUiConfig {
    return {
        topMenu: {
            visible: true,
            createAdButton: true,
            authBlock: false,
            cabinetMode: true,
            wrapInCard: true,
        },
        adsSearch: false,
        footer: {
            visible: true,
            navLinks: false,
        },
        sideMenuType: 'profile',
        miniChat: true,
    }
}

// Настройка глобальных элементов интерфейса приложения для кабинета пользователя.
export function useConfigureCabinetUi(
    // В config заданы значения по умолчанию.
    // В нормальном случае нужно изменить config и вернуть его.
    // Если вернуть null, то UI останется без изменений.
    modifyConfig: (config: DefaultCabinetUiConfig) => CabinetUiConfig | null = config => config,
    deps: DependencyList = []
) {
    const {setSideMenuType} = useCabinetContextApi()

    useConfigureUi(() => {
        const config = modifyConfig(getDefaults())
        if (!config) {
            return null
        }
        const other = config.other
        config.other = () => {
            other?.()
            // Боковое меню в кабинете пользователя.
            setSideMenuType(config.sideMenuType ?? 'profile')
        }
        return config
    }, deps)
}
